// Scroll lock state management - no React hooks, safe to use anywhere
// Build ID for verification
export const BUILD_ID = '2024-02-04-L';

// Singleton state: tracks whether app intends scroll to be locked
let intendedLockCount = 0;
const lockReasons = new Set<string>();

// Register a lock reason (e.g., 'cookie-modal', 'mobile-menu')
export const registerScrollLock = (reason: string) => {
  if (!lockReasons.has(reason)) {
    lockReasons.add(reason);
    intendedLockCount++;
  }
};

// Unregister a lock reason
export const unregisterScrollLock = (reason: string) => {
  if (lockReasons.has(reason)) {
    lockReasons.delete(reason);
    intendedLockCount = Math.max(0, intendedLockCount - 1);
  }
};

// Check if scroll should be locked
export const isScrollIntendedLocked = () => intendedLockCount > 0;

// Get current lock reasons (for debugging)
export const getLockReasons = () => Array.from(lockReasons);

// Force unlock all (for recovery)
export const forceUnlockAllReasons = () => {
  lockReasons.clear();
  intendedLockCount = 0;
};

// Clear scroll-blocking styles from an element
export const clearScrollStyles = (el: HTMLElement) => {
  el.style.overflow = '';
  el.style.overflowX = '';
  el.style.overflowY = '';
  el.style.position = '';
  el.style.top = '';
  el.style.width = '';
  el.style.height = '';
  el.style.touchAction = '';
  el.removeAttribute('data-scroll-locked');
};

// Enforce the intended scroll state - OPTIMIZED to minimize getComputedStyle calls
export const enforceScrollState = () => {
  // Early exit if scroll should be locked - nothing to enforce
  if (isScrollIntendedLocked()) return;

  const html = document.documentElement;
  const body = document.body;

  // Quick check: only proceed if inline styles suggest a problem
  const hasInlineBlock =
    html.style.overflow === 'hidden' ||
    html.style.overflowY === 'hidden' ||
    body.style.position === 'fixed' ||
    body.style.overflow === 'hidden' ||
    body.style.overflowY === 'hidden';

  if (!hasInlineBlock) {
    // No suspicious inline styles - skip expensive computed style checks
    return;
  }

  // Clear the blocking inline styles
  clearScrollStyles(html);
  clearScrollStyles(body);

  // Only use getComputedStyle as last resort if we had to clear styles
  const htmlComputed = getComputedStyle(html);
  if (htmlComputed.overflow === 'hidden' || htmlComputed.overflowY === 'hidden') {
    html.style.overflowY = 'auto';
  }
};

// Force unlock all and enforce
export const forceUnlockAll = () => {
  forceUnlockAllReasons();
  enforceScrollState();
};
